import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { fetchAffiliates } from '@/lib/affiliates'
import {
  fetchDistributors,
  fetchDistributorBalanceTransactions,
  fetchDistributorAffiliates,
  recordDistributorPayout,
} from '@/lib/distributors'
import { ApiClientError } from '@/lib/api/client'
import type { Affiliate } from '@/types/affiliates'
import type { Distributor, DistributorBalanceTransaction } from '@/types/distributors'
import styles from './DistribuidoresSaldoPage.module.css'

function formatCurrency(value: number) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

interface ChildAffiliateBalanceRow {
  registrationId: number
  affiliateName: string
  trackingCode: string
  balance: number | null
  payoutMethod: string | null
}

export function DistribuidoresSaldoPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const distributorIdParam = searchParams.get('distributor_id')

  const [distributors, setDistributors] = useState<Distributor[]>([])
  const [institutionAffiliates, setInstitutionAffiliates] = useState<Affiliate[]>([])
  const [childAffiliates, setChildAffiliates] = useState<ChildAffiliateBalanceRow[]>([])
  const [selectedId, setSelectedId] = useState<number | ''>('')
  const [transactions, setTransactions] = useState<DistributorBalanceTransaction[]>([])
  const [pagination, setPagination] = useState<{
    current_page: number
    per_page: number
    total: number
    last_page: number
  } | null>(null)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isLoadingChildren, setIsLoadingChildren] = useState(false)

  const [payoutAmount, setPayoutAmount] = useState('')
  const [payoutNote, setPayoutNote] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const manualDistributors = useMemo(
    () => distributors.filter((d) => d.payout_method === 'manual'),
    [distributors]
  )

  const affiliateById = useMemo(() => {
    const map = new Map<number, Affiliate>()
    for (const affiliate of institutionAffiliates) {
      map.set(affiliate.id, affiliate)
    }
    return map
  }, [institutionAffiliates])

  const loadDistributors = useCallback(async () => {
    const res = await fetchDistributors()
    setDistributors(res.distributors)
  }, [])

  const loadInstitutionAffiliates = useCallback(async () => {
    const res = await fetchAffiliates()
    setInstitutionAffiliates(res.affiliates)
  }, [])

  const loadChildAffiliates = useCallback(
    async (distributorId: number) => {
      setIsLoadingChildren(true)
      try {
        const res = await fetchDistributorAffiliates(distributorId)
        const rows: ChildAffiliateBalanceRow[] = res.registrations.map((registration) => {
          const affiliate = affiliateById.get(registration.institution_affiliate_id)
          return {
            registrationId: registration.id,
            affiliateName: registration.affiliate_name,
            trackingCode: registration.tracking_code,
            balance:
              affiliate?.payout_method === 'manual' ? affiliate.balance : null,
            payoutMethod: affiliate?.payout_method ?? null,
          }
        })
        setChildAffiliates(rows)
      } catch {
        setChildAffiliates([])
      } finally {
        setIsLoadingChildren(false)
      }
    },
    [affiliateById]
  )

  const loadLedger = useCallback(async (distributorId: number, p: number) => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetchDistributorBalanceTransactions(distributorId, {
        page: p,
        per_page: 20,
      })
      setTransactions(res.items)
      setPagination(res.pagination)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar extrato.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void (async () => {
      try {
        await Promise.all([loadDistributors(), loadInstitutionAffiliates()])
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar dados.')
      }
    })()
  }, [loadDistributors, loadInstitutionAffiliates])

  useEffect(() => {
    if (distributorIdParam && manualDistributors.length > 0) {
      const id = Number.parseInt(distributorIdParam, 10)
      if (Number.isFinite(id) && manualDistributors.some((d) => d.id === id)) {
        setSelectedId(id)
      }
    }
  }, [distributorIdParam, manualDistributors])

  useEffect(() => {
    if (selectedId === '') {
      setTransactions([])
      setPagination(null)
      setChildAffiliates([])
      return
    }
    void loadLedger(selectedId, page)
  }, [selectedId, page, loadLedger])

  useEffect(() => {
    if (selectedId === '' || institutionAffiliates.length === 0) {
      return
    }
    void loadChildAffiliates(selectedId)
  }, [selectedId, institutionAffiliates, loadChildAffiliates])

  const handleSelectDistributor = (id: number | '') => {
    setSelectedId(id)
    setPage(1)
    setFormError(null)
    setPayoutAmount('')
    setPayoutNote('')
    if (id === '') {
      setSearchParams({})
    } else {
      setSearchParams({ distributor_id: String(id) })
    }
  }

  const handlePayout = async (e: React.FormEvent) => {
    e.preventDefault()
    if (selectedId === '') return
    setFormError(null)
    const n = parseFloat(payoutAmount.replace(',', '.'))
    if (Number.isNaN(n) || n < 0.01) {
      setFormError('Informe um valor válido (mín. R$ 0,01).')
      return
    }
    setIsSubmitting(true)
    try {
      await recordDistributorPayout(selectedId, {
        amount: n,
        note: payoutNote.trim() || null,
      })
      setPayoutAmount('')
      setPayoutNote('')
      await loadDistributors()
      await loadLedger(selectedId, 1)
      await loadChildAffiliates(selectedId)
      setPage(1)
    } catch (err) {
      setFormError(
        err instanceof ApiClientError
          ? err.message
          : 'Não foi possível registrar o pagamento.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedDistributor = useMemo(
    () =>
      selectedId === '' ? null : distributors.find((d) => d.id === selectedId) ?? null,
    [distributors, selectedId]
  )
  const currentBalance = selectedDistributor?.balance ?? null

  const isDeepLinkToDistributor =
    distributorIdParam != null && distributorIdParam !== ''

  const manualChildWithBalance = childAffiliates.filter((row) => row.balance !== null)

  return (
    <div className={styles.page}>
      {!isDeepLinkToDistributor && (
        <>
          <p className={styles.intro}>
            Distribuidores em <strong>pagamento manual</strong> acumulam comissão como saldo interno
            após vendas confirmadas. Registre aqui os pagamentos que você efetuar fora da plataforma
            para dar baixa no saldo do distribuidor. Afiliados vinculados com pagamento manual também
            exibem saldo abaixo.
          </p>

          <div className={styles.toolbar}>
            <label className={styles.selectLabel}>
              <span>Distribuidor (manual)</span>
              <select
                className={styles.select}
                value={selectedId === '' ? '' : String(selectedId)}
                onChange={(e) => {
                  const v = e.target.value
                  handleSelectDistributor(v === '' ? '' : Number.parseInt(v, 10))
                }}
              >
                <option value="">Selecione…</option>
                {manualDistributors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.distributor_name} — saldo {formatCurrency(d.balance)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </>
      )}

      {manualDistributors.length === 0 && !isLoading && (
        <div className={styles.empty}>
          <Icon icon="mdi:wallet-outline" className={styles.emptyIcon} />
          <p>
            Nenhum distribuidor com pagamento manual. Crie ou edite um distribuidor na lista e escolha
            “Manual”.
          </p>
        </div>
      )}

      {selectedId !== '' && (
        <>
          <div className={styles.balanceCard}>
            <span className={styles.balanceLabel}>Saldo do distribuidor</span>
            <span className={styles.balanceValue}>
              {currentBalance !== null ? formatCurrency(currentBalance) : '—'}
            </span>
          </div>

          {(isLoadingChildren || manualChildWithBalance.length > 0) && (
            <>
              <h3 className={styles.sectionTitle}>Saldos dos afiliados vinculados</h3>
              {isLoadingChildren ? (
                <div className={styles.centeredMuted}>Carregando afiliados…</div>
              ) : manualChildWithBalance.length === 0 ? (
                <div className={styles.centeredMuted}>
                  Nenhum afiliado vinculado com pagamento manual.
                </div>
              ) : (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Afiliado</th>
                        <th>Código</th>
                        <th>Saldo interno</th>
                      </tr>
                    </thead>
                    <tbody>
                      {manualChildWithBalance.map((row) => (
                        <tr key={row.registrationId}>
                          <td>{row.affiliateName}</td>
                          <td className={styles.noteCell}>{row.trackingCode}</td>
                          <td className={styles.creditAmt}>
                            {row.balance !== null ? formatCurrency(row.balance) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          <form className={styles.payoutForm} onSubmit={(e) => void handlePayout(e)}>
            <h3 className={styles.formTitle}>Registrar pagamento ao distribuidor (baixa)</h3>
            <div className={styles.formRow}>
              <label className={styles.field}>
                <span>Valor pago (R$) *</span>
                <input
                  className={styles.input}
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                />
              </label>
              <label className={styles.fieldGrow}>
                <span>Observação (opcional)</span>
                <input
                  className={styles.input}
                  type="text"
                  placeholder="Ex.: PIX enviado, TED, etc."
                  value={payoutNote}
                  onChange={(e) => setPayoutNote(e.target.value)}
                  maxLength={2000}
                />
              </label>
            </div>
            {formError && (
              <div className={styles.formError}>
                <Icon icon="mdi:alert-circle-outline" />
                {formError}
              </div>
            )}
            <button type="submit" className={styles.btnSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Salvando…' : 'Confirmar baixa'}
            </button>
          </form>

          <h3 className={styles.sectionTitle}>Extrato do distribuidor</h3>
          {isLoading ? (
            <div className={styles.centered}>
              <Icon icon="mdi:loading" className={styles.spinner} />
              Carregando…
            </div>
          ) : error ? (
            <div className={styles.centered}>{error}</div>
          ) : transactions.length === 0 ? (
            <div className={styles.centeredMuted}>Nenhuma movimentação ainda.</div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Tipo</th>
                    <th>Valor</th>
                    <th>Detalhe</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t) => (
                    <tr key={t.id}>
                      <td>{formatDate(t.created_at)}</td>
                      <td>
                        {t.type === 'commission_credit' ? (
                          <span className={styles.badgeCredit}>Crédito (venda)</span>
                        ) : t.type === 'simulation_credit' ? (
                          <span className={styles.badgeSim}>Crédito (simulação)</span>
                        ) : (
                          <span className={styles.badgeDebit}>Pagamento (baixa)</span>
                        )}
                      </td>
                      <td className={t.type === 'payout_debit' ? styles.debitAmt : styles.creditAmt}>
                        {t.type === 'payout_debit' ? '−' : '+'}
                        {formatCurrency(t.amount)}
                      </td>
                      <td className={styles.noteCell}>
                        {t.type === 'payout_debit' && t.note ? t.note : ''}
                        {t.type === 'commission_credit' && t.payment_id != null && (
                          <span className={styles.muted}>Pagamento #{t.payment_id}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pagination && pagination.last_page > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </button>
              <span>
                Página {pagination.current_page} de {pagination.last_page}
              </span>
              <button
                type="button"
                className={styles.pageBtn}
                disabled={page >= pagination.last_page}
                onClick={() => setPage((p) => p + 1)}
              >
                Próxima
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
